import React, { useCallback, useEffect, useState, useRef } from 'react';
import { getChecklistSessionsByCar } from '../../../api/checklistsApi';
import ChecklistModal from '../checklist/ChecklistModal';
import type { ChecklistType } from '../../../types/checklist';
import { checklistTypeLabel } from '../../../types/checklist';
import '../status-history/StatusHistoryModal.css';

interface ChecklistHistoryModalProps {
  isOpen: boolean;
  carId: string;
  carPlate: string;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen?: boolean;
  /**
   * A tela hospedeira permite entrar em modo de edição do checklist? (at-65, BUG-V11)
   *
   * Default `false` — este modal é histórico, e o caso comum é consulta. O **Chefe de
   * Oficina** passa `true` porque a tela dele é operacional e a edição de checklist é um
   * caminho legítimo de lá; a Tela de Controle não passa, e por isso vira leitura de fato.
   */
  permitirEdicao?: boolean;
}

interface ChecklistSessionItem {
  id: string;
  carId: string;
  type: ChecklistType;
  status: 'DRAFT' | 'COMPLETED';
  createdAt: string;
  completedAt?: string | null;
}

const ChecklistHistoryModal: React.FC<ChecklistHistoryModalProps> = ({
  isOpen,
  carId,
  carPlate,
  onCancel,
  sidebarRef,
  permitirEdicao = false,
}) => {
  const [sessions, setSessions] = useState<ChecklistSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // States for viewing a specific checklist session
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionType, setSelectedSessionType] = useState<ChecklistType | null>(null);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getChecklistSessionsByCar(carId);
      setSessions(data);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar o histórico de checklists');
    } finally {
      setLoading(false);
    }
  }, [carId]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, fetchHistory]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      
      // If the inner read-only ChecklistModal is open, don't close the history modal from outside clicks
      if (isChecklistOpen) return;

      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef, isChecklistOpen]);

  const handleOpenChecklist = (sessionId: string, type: ChecklistType) => {
    setSelectedSessionId(sessionId);
    setSelectedSessionType(type);
    setIsChecklistOpen(true);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="add-car-modal-overlay modal-overlay--empilhado">
        <div className="parts-history-modal" ref={modalRef} style={{ width: 'min(750px, calc(100vw - 32px))' }}>
          <div className="parts-history-title">
            Histórico de Checklists <span style={{ fontSize: '15px', color: 'var(--tema-texto-3, #667085)', fontWeight: 'normal' }}>— Placa: {carPlate}</span>
          </div>

          <div className="parts-history-grid" style={{ marginBottom: '10px' }}>
            <div className="parts-history-row parts-history-row--4 parts-history-header">
              <div>Tipo</div>
              <div>Status</div>
              <div>Data de Início</div>
              <div>Ações</div>
            </div>

            {loading && <div className="parts-history-empty">Carregando histórico...</div>}
            {!loading && error && <div className="parts-history-empty" style={{ color: '#d32f2f' }}>{error}</div>}
            {!loading && !error && sessions.length === 0 && (
              <div className="parts-history-empty">Nenhum checklist realizado para este veículo.</div>
            )}

            {!loading && !error && sessions.map((session) => (
              <div 
                className="parts-history-row parts-history-row--4" 
                key={session.id}
                style={{ height: '52px' }}
              >
                <div style={{ fontWeight: 600, color: 'var(--tema-texto, #101828)' }}>
                  {`Checklist de ${checklistTypeLabel[session.type] || session.type}`}
                </div>
                <div>
                  <span 
                    style={{
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      backgroundColor: session.status === 'COMPLETED' ? 'var(--tema-tinta-verde, #d1fae5)' : 'var(--tema-tinta-laranja, #ffeedf)',
                      color: session.status === 'COMPLETED' ? 'var(--tema-texto-verde, #065f46)' : '#ea5d0e'
                    }}
                  >
                    {session.status === 'COMPLETED' ? 'Concluído' : 'Rascunho'}
                  </span>
                </div>
                <div>{new Date(session.createdAt).toLocaleString('pt-BR')}</div>
                <div>
                  <button
                    type="button"
                    onClick={() => handleOpenChecklist(session.id, session.type)}
                    style={{
                      background: '#ea5d0e',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      padding: '6px 12px',
                      fontFamily: "'AmpleSoft-Bold', sans-serif",
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = '#d14d05')}
                    onMouseOut={(e) => (e.currentTarget.style.background = '#ea5d0e')}
                  >
                    Visualizar
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'var(--tema-superficie, #fff)',
                border: '1.5px solid var(--tema-linha-2, #d1d5db)',
                borderRadius: '8px',
                color: 'var(--tema-texto-2, #4b5563)',
                padding: '8px 20px',
                fontFamily: "'AmpleSoft-Bold', sans-serif",
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#9ca3af';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>

      {isChecklistOpen && selectedSessionId && selectedSessionType && (
        <ChecklistModal
          isOpen={isChecklistOpen}
          onClose={() => setIsChecklistOpen(false)}
          carId={carId}
          sessionId={selectedSessionId}
          tipoServico={selectedSessionType}
          readOnly={true}
          permitirEdicao={permitirEdicao}
        />
      )}
    </>
  );
};

export default ChecklistHistoryModal;
